import { z } from 'zod'
import { AgentAvatar } from '@algoboo/schema/api'
import { EXPRESSIONS, EMBLEM_OPTIONS, STUDIO_FINISHES, fittedHeadwear, fitAvatar, PALETTE_IDS } from '@algoboo/ui'

export const STUDIO_STORAGE_KEY = 'algoboo:agent-studio:v1'
export const MAX_SAVED_LOOKS = 12

export const STARTER_LOOKS: { name: string; avatar: AgentAvatar }[] = [
  { name: 'Glim Scout', avatar: { family: 'glim', palette: 'sage', expression: 'curious', material: 'jelly', loadout: 'lightning-bolt' } },
  { name: 'Glim Dusk', avatar: { family: 'glim', palette: 'plum', expression: 'happy', material: 'jelly', loadout: 'star' } },
  { name: 'Glim Cozy', avatar: { family: 'glim', palette: 'lilac', expression: 'sleepy', material: 'frosted' } },
  { name: 'Aura Orbit', avatar: { family: 'aura', palette: 'peach', expression: 'curious', material: 'jelly', headwear: 'antenna' } },
  { name: 'Aura Frost', avatar: { family: 'aura', palette: 'sky', expression: 'sleepy', material: 'frosted', headwear: 'beanie' } },
  { name: 'Aura Bloom', avatar: { family: 'aura', palette: 'pearl', expression: 'happy', material: 'jelly', loadout: 'star' } },
]

export const DEFAULT_STUDIO_AVATAR: AgentAvatar = { family: 'glim', palette: 'ivory', expression: 'smile', material: 'jelly' }

const SavedLook = z.object({ id: z.string().min(1).max(80), name: z.string().min(1).max(40), avatar: AgentAvatar })
const StudioDocument = z.object({ version: z.literal(1), avatar: AgentAvatar, looks: z.array(SavedLook).max(MAX_SAVED_LOOKS) })
export type SavedLook = z.infer<typeof SavedLook>
export type StudioDocument = z.infer<typeof StudioDocument>

export function readStudio(storage: Pick<Storage, 'getItem'>): StudioDocument {
  try {
    const data = StudioDocument.safeParse(JSON.parse(storage.getItem(STUDIO_STORAGE_KEY) ?? 'null'))
    if (data.success) return { ...data.data, avatar: fitAvatar(data.data.avatar) }
  } catch { /* Invalid or unavailable browser storage falls back to a fresh design. */ }
  return { version: 1, avatar: { ...DEFAULT_STUDIO_AVATAR }, looks: [] }
}

/** Only structured cosmetic data is accepted from the Studio handoff URL. */
export function decodeStudioLook(value: unknown): AgentAvatar | undefined {
  if (typeof value !== 'string' || value.length > 2000) return undefined
  try {
    const result = AgentAvatar.safeParse(JSON.parse(value))
    return result.success ? result.data : undefined
  } catch { return undefined }
}

export function avatarKey(avatar: AgentAvatar): string {
  return JSON.stringify([avatar.family, avatar.palette, avatar.expression, avatar.material, avatar.headwear, avatar.outfit, avatar.loadout])
}

export interface LookHistory { past: AgentAvatar[]; present: AgentAvatar; future: AgentAvatar[] }
export type LookAction = { type: 'change'; avatar: AgentAvatar } | { type: 'undo' | 'redo' }

export function lookHistory(state: LookHistory, action: LookAction): LookHistory {
  if (action.type === 'change') {
    if (avatarKey(state.present) === avatarKey(action.avatar)) return state
    return { past: [...state.past, state.present].slice(-50), present: fitAvatar(action.avatar), future: [] }
  }
  if (action.type === 'undo') {
    const previous = state.past.at(-1)
    return previous ? { past: state.past.slice(0, -1), present: previous, future: [state.present, ...state.future] } : state
  }
  const next = state.future[0]
  return next ? { past: [...state.past, state.present], present: next, future: state.future.slice(1) } : state
}

export function shuffleTraits(family: AgentAvatar['family']): AgentAvatar {
  const pick = <T,>(options: readonly T[]): T => options[Math.floor(Math.random() * options.length)]!
  const avatar: AgentAvatar = { family, palette: pick(PALETTE_IDS), expression: pick(EXPRESSIONS), material: pick(STUDIO_FINISHES) }
  for (const [slot, options] of [['headwear', fittedHeadwear(family)], ['loadout', EMBLEM_OPTIONS]] as const) {
    const part = pick([undefined, undefined, ...options])
    if (part) avatar[slot] = part.id
  }
  return fitAvatar(avatar)
}
